import clsx from 'clsx'
import { FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import {
  DomainSelector,
  useTopbarComponent,
  usePaginatedProjects,
  useGlobalState,
  useLocalStorage,
  useContentRefreshDomains,
} from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useDocItems } from 'utils/hooks/useDocItems'

import CreateProject from 'components/CreateProject'
import PropertySelectorModal from 'components/PropertySelectorModal'
import CMPBanner from 'components/Banners/CMPBanner/CMPBanner'
import MarketplaceStagesBanner from 'components/Banners/MarketplaceStagesBanner/MarketplaceStagesBanner'
import CreateMenu from 'components/CreateMenu'
import CreateContentPopover from 'components/CreateContentPopover'
import { format, subMonths } from 'date-fns'

import { getCmpBannerText } from './getCmpBannerText'
import CreateContentCards from './CreateContentCards/CreateContentCards'
import RecentItems from './RecentItems/RecentItems'
import RecentProjects from './RecentProjects/RecentProjects'
import Stats from './Stats/Stats'
import CmpStats from './CmpStats'
import CmpStatsDefault from './CmpStatsDefault'
import AlertBarWrapper from './AlertBar/AlertBarWrapper'

import s from './Home.module.css'

const PROJECT_PER_PAGE = 6

const Home: FC = () => {
  const previousMonthWithYear = format(subMonths(new Date(), 1), 'MMM yyyy')
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [showCreateContentPopover, setShowCreateContentPopover] =
    useState(false)
  const companyId = useSelectedCompanyId()
  const {
    pendingSidebarCountKeys,
    setPendingSidebarCountKeys,
    permissions,
    subscription,
    domainId,
  } = useGlobalState()
  const [lsDomainId] = useLocalStorage('domain_id', '')

  const {
    data: domains,
    isLoading: isLoadingDomains,
    isGoogleConnected,
    refetch: refetchDomains,
    connectedGoogleAccount,
  } = useContentRefreshDomains(companyId)

  useTopbarComponent({ header: 'Home' })
  const history = useHistory()
  const hasMarketplacePermission = !!permissions?.marketplace
  const hasCmpPermission = !!permissions?.cmp
  const cmpBannerText = getCmpBannerText(subscription)
  const hasUpgradedTrialStatus = subscription?.trialStatus === 'upgraded'
  const isPtGrandfathered = subscription?.key === 'pt_grandfathered'

  const { records: recentItems } = useDocItems({
    companyId,
    pageSize: 10,
  })

  const selectedDomainId = domainId ?? lsDomainId ?? domains?.[0]?.id

  const handleProjectCardClick = (redirectTo: string) => {
    history.push(`${redirectTo}`)
  }

  const { projects, isProjectsLoading, totalProjects, refetchProjects } =
    usePaginatedProjects(
      {
        current_page: 0,
        company_id: companyId,
        page_size: PROJECT_PER_PAGE,
        pagination_enabled: true,
      },
      { enabled: Boolean(companyId) }
    )

  useEffect(() => {
    if (pendingSidebarCountKeys.includes('allProjectsCount')) {
      refetchProjects()
      setPendingSidebarCountKeys(['allProjectsCount'], 'remove')
    }
  }, [refetchProjects, pendingSidebarCountKeys, setPendingSidebarCountKeys])

  return (
    <div className={s.bodyWrap}>
      <Helmet>
        <title>Home</title>
      </Helmet>

      {hasMarketplacePermission && (
        <AlertBarWrapper
          companyId={companyId}
          hasNoProjects={!isProjectsLoading && totalProjects === 0}
        />
      )}

      <div className={s.container}>
        <div
          className={clsx(s.stats, {
            [s.inlineStats]: hasMarketplacePermission && hasCmpPermission,
          })}
        >
          {!!connectedGoogleAccount && (
            <PropertySelectorModal
              domains={domains}
              connectedAccount={connectedGoogleAccount}
              companyId={companyId}
              onSuccess={refetchDomains}
            />
          )}
          {hasCmpPermission &&
            !isPtGrandfathered &&
            (isGoogleConnected ? (
              <div>
                <div className={s.domainSelector}>
                  <DomainSelector companyId={companyId} isControlled={false} />
                  <span className={s.roiTimeRange}>
                    ({previousMonthWithYear})
                  </span>
                </div>
                <CmpStats
                  selectedDomainId={selectedDomainId}
                  width={hasMarketplacePermission ? 'half' : 'full'}
                />
              </div>
            ) : (
              <CmpStatsDefault
                domains={domains}
                onAddDomain={refetchDomains}
                width={hasMarketplacePermission ? 'half' : 'full'}
                isLoadingDomains={isLoadingDomains}
              />
            ))}
          {hasMarketplacePermission && (
            <Stats
              companyId={companyId}
              width={hasCmpPermission ? 'half' : 'full'}
              hasNoProjects={!isProjectsLoading && !projects?.length}
              onCreateProject={() => setShowProjectModal(true)}
            />
          )}
        </div>
      </div>

      <div className={s.container}>
        {!isPtGrandfathered && (
          <>
            <div className={s.header}>
              <span className={s.title}>What do you want to create today?</span>
            </div>
            <CreateContentCards />
          </>
        )}
        {isPtGrandfathered && (
          <>
            <div className={s.header}>
              <span className={s.title}>What do you want to create today?</span>
            </div>
            <CreateContentCards isPtGrandfathered={true} />
          </>
        )}
      </div>
      {isPtGrandfathered && !!recentItems?.length && (
        <div className={s.container}>
          <div className={s.header}>
            <span className={s.title}>Recent Items</span>
            <div className={s.btnContainer}>
              <CreateMenu subMenuPlacement="left" hideOrderOption={true}>
                <button
                  className={s.redirectLink}
                  onClick={() => handleProjectCardClick('/projects/items')}
                >
                  View All Items
                </button>
              </CreateMenu>
            </div>
          </div>
          <RecentItems items={recentItems} companyId={companyId} />
        </div>
      )}

      <div className={s.container}>
        <div className={s.header}>
          <span className={s.title}>Recent Projects</span>
          {totalProjects > 0 && (
            <div className={s.btnContainer}>
              <button
                className={s.redirectLink}
                onClick={() => handleProjectCardClick('/projects')}
              >
                View All
              </button>
            </div>
          )}
        </div>
        <RecentProjects
          projects={projects}
          isProjectsLoading={isProjectsLoading}
          totalProjects={totalProjects}
          setShowProjectModal={setShowProjectModal}
        />
      </div>

      <div className={s.bannerSection}>
        {!hasUpgradedTrialStatus && (
          <div
            className={clsx(s.bannerWrapper, {
              [s.showOneBanner]: hasMarketplacePermission,
            })}
          >
            <CMPBanner
              className={s.cmpBanner}
              primaryBtnText={cmpBannerText.primaryBtnDescription}
              bannerStateDescriptor={cmpBannerText.bannerStateDescriptor}
              title={cmpBannerText.title}
              description={cmpBannerText.description}
              primaryBtnLink={cmpBannerText.primaryBtnLink}
              isCtaDisabled={false}
            />
          </div>
        )}
        {!hasMarketplacePermission && (
          <div
            className={clsx(s.bannerWrapper, {
              [s.showOneBanner]: hasUpgradedTrialStatus,
            })}
          >
            <MarketplaceStagesBanner className={s.marketBanner} />
          </div>
        )}
      </div>
      <CreateProject
        companyId={companyId}
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
      />
      <CreateContentPopover
        showCreateContentPopover={showCreateContentPopover}
        setShowCreateContentPopover={setShowCreateContentPopover}
      />
    </div>
  )
}

export default Home
